import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

/**
 * Normalized Real-time Conversations Store
 * Single source of truth for all conversation data
 * No tab-level caching - everything is real-time via WebSocket
 */

/** Invited agent id: assignment field or participant with role Pending Invite */
export const getPendingInviteTargetUserId = (c) => {
  if (!c) return null;
  const fromAssignment = c.assignment?.pending_invite_to;
  if (fromAssignment != null && fromAssignment !== '') return Number(fromAssignment);
  const p = (c.participants || []).find(
    (x) => x.role === 'Pending Invite' || x.role === 'PENDING_INVITE'
  );
  return p?.user != null ? Number(p.user) : null;
};

const hasPendingInviteForUser = (conversation, me) => {
  if (!conversation || me == null) return false;

  // Prefer explicit invite target when present
  const explicitInviteTarget =
    conversation.invite_target ?? conversation.assignment?.pending_invite_to;
  if (
    explicitInviteTarget != null &&
    !Number.isNaN(Number(explicitInviteTarget)) &&
    Number(explicitInviteTarget) === Number(me)
  ) {
    return true;
  }

  // Fallback: support multiple pending invite participants in one conversation
  const participants = Array.isArray(conversation.participants) ? conversation.participants : [];
  return participants.some((participant) => {
    if (participant?.user == null) return false;
    const role = String(participant.role || '').trim().toLowerCase().replace(/[\s_]+/g, ' ');
    return role === 'pending invite' && Number(participant.user) === Number(me);
  });
};

const normalizeStatus = (status) => {
  if (status == null) return '';
  return String(status)
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, ' ');
};

const isAwaitingAgentStatus = (status) => normalizeStatus(status) === 'Awaiting Agent';

// Canonical tab membership rules (one place, used everywhere)
export const tabRules = {
  All: (c) => {
     // Check if owner is null or undefined
     const owner = c.owner;
     const hasNoOwner = !owner || (typeof owner === 'object' && !owner.id) || owner === null;
     
     // Exclude conversations with Awaiting Agent status from All tab
     const status = c.status;
     const isAwaitingAgent = status === 'AWAITING_AGENT' || status === 'Awaiting Agent' || 
                            status === 'awaiting_agent' || status === 'Awaiting_Agent';
     
     return hasNoOwner && !isAwaitingAgent;
  },
  My: (c, { me }) => {
    if (
      c.is_alert ||
      c.queue === 'alerts' ||
      c.queue === 'Alerts' ||
      isAwaitingAgentStatus(c.status)
    ) {
      return false;
    }

    const owner = c.owner;
    const ownerId = typeof owner === 'object' ? owner?.id : owner;
    const isCurrentUserOwner = Number(ownerId) === Number(me);
    const transferTarget = c.transfer_target || c.assignment?.pending_transfer_to;
    const isPendingTransferStatus =
      c.status === 'pending_transfer' ||
      c.status === 'PENDING_TRANSFER' ||
      c.status === 'Pending Transfer';
    const isCurrentUserTransferTarget =
      transferTarget != null && Number(transferTarget) === Number(me);

    // Also include conversations where current user is an active participant.
    // Invite-accept flows can keep owner unchanged while still belonging to "My" for invitee.
    const participants = Array.isArray(c.participants) ? c.participants : [];
    const isActiveParticipant = participants.some((p) => {
      if (p?.user == null) return false;
      const role = p.role;
      const isPendingInviteRole =
        role === 'Pending Invite' || role === 'PENDING_INVITE' || role === 'pending_invite';
      return (
        Number(p.user) === Number(me) &&
        p.is_active === true &&
        !isPendingInviteRole
      );
    });

    // Pending transfer should appear in My for sender/owner only.
    // Receiver/target should see it in Inbox only.
    if (isPendingTransferStatus && isCurrentUserTransferTarget && !isCurrentUserOwner) {
      return false;
    }

    return isCurrentUserOwner || isActiveParticipant;
  },
  Inbox: (c, { me }) => {
    const transferTarget = c.transfer_target || c.assignment?.pending_transfer_to;
    const isPendingTransfer =
      c.status === 'pending_transfer' ||
      c.status === 'PENDING_TRANSFER' ||
      c.status === 'Pending Transfer';
    if (isPendingTransfer && Number(transferTarget) === Number(me)) {
      return true;
    }
    const isPendingInvite =
      c.status === 'pending_invite' ||
      c.status === 'PENDING_INVITE' ||
      c.status === 'Pending Invite';
    return isPendingInvite && hasPendingInviteForUser(c, me);
  },
  Alerts: (c) => {
    const status = c.status;
    const isAwaitingAgent = status === 'AWAITING_AGENT' || status === 'Awaiting Agent' || 
                           status === 'awaiting_agent' || status === 'Awaiting_Agent';
    
    return !!(c.is_alert || c.queue === 'alerts' || c.queue === 'Alerts' || isAwaitingAgent);
  },
};

// Sort conversations by last message time
const sortByLastMsg = (ids, byId) =>
  ids.slice().sort((a, b) =>
    new Date(byId[b]?.updated_at || 0) -
    new Date(byId[a]?.updated_at || 0)
  );

export const useConversationsStore = create(
  immer((set, get) => ({
    // Normalized conversation data
    byId: {},
    
    // Tab lists (computed from rules)
    listAll: [],
    listMy: [],
    listInbox: [],
    listAlerts: [],
    
    // Minimal persistence: which conversation is open per tab
    selectedByTab: { 
      All: null, 
      My: null, 
      Inbox: null, 
      Alerts: null 
    },
    
    // Current user context
    currentUser: null,
    
    // Set current user context
    setCurrentUser: (user) => set(draft => {
      draft.currentUser = user;
    }),
    
    // One-time snapshot load (initial load or after reconnect)
    hydrateSnapshot: (conversations, user) => set(draft => {
      console.log('🔄 Hydrating conversations store with snapshot:', {
        conversationsCount: conversations.length,
        userId: user?.id,
        conversationsType: typeof conversations,
        isArray: Array.isArray(conversations)
      });
      
      if (conversations.length > 0) {
        console.log('📊 Sample conversation data:', {
          first: {
            id: conversations[0].id,
            owner: conversations[0].owner,
            ownerType: typeof conversations[0].owner,
            primary_contact: conversations[0].primary_contact,
            status: conversations[0].status,
            hasId: !!conversations[0].id
          },
          sampleIds: conversations.slice(0, 5).map(c => c?.id)
        });
      } else {
        console.warn('⚠️ No conversations provided to hydrateSnapshot!');
      }
      
      draft.byId = {};
      draft.currentUser = user;
      
      // Normalize conversations into byId
      let normalizedCount = 0;
      conversations.forEach(c => {
        if (c && c.id) {
          draft.byId[c.id] = c;
          normalizedCount++;
        } else {
          console.warn('⚠️ Skipping invalid conversation:', c);
        }
      });
      
      console.log(`📦 Normalized ${normalizedCount} conversations into byId`);
      
      const ids = conversations.filter(c => c && c.id).map(c => c.id);
      const context = { me: user?.id };
      
      console.log('🔍 Building tab lists with context:', {
        userId: context.me,
        totalIds: ids.length,
        sampleIds: ids.slice(0, 5)
      });
      
      // Build tab lists using canonical rules
      const filt = (rule) => ids.filter(id => {
        const conversation = draft.byId[id];
        if (!conversation) return false;
        const matches = rule(conversation, context);
        return matches;
      });
      
      draft.listAll = sortByLastMsg(filt(tabRules.All), draft.byId);
      draft.listMy = sortByLastMsg(filt(tabRules.My), draft.byId);
      draft.listInbox = sortByLastMsg(filt(tabRules.Inbox), draft.byId);
      draft.listAlerts = sortByLastMsg(filt(tabRules.Alerts), draft.byId);
      
      console.log('✅ Store hydrated:', {
        total: conversations.length,
        All: draft.listAll.length,
        My: draft.listMy.length,
        Inbox: draft.listInbox.length,
        Alerts: draft.listAlerts.length
      });
      
      // Log sample from each tab
      if (draft.listAll.length > 0) {
        console.log('📋 Sample All tab conversation:', draft.byId[draft.listAll[0]]?.primary_contact?.name);
      }
      if (draft.listMy.length > 0) {
        console.log('📋 Sample My tab conversation:', draft.byId[draft.listMy[0]]?.primary_contact?.name);
      }
    }),
    
    // Single source of truth for every WebSocket update
    upsertFromEvent: (chat, activeTab, clearIfOpen) => set(draft => {
      console.log('🔄 Upserting conversation from WebSocket event:', chat.id);
      
      const context = { me: draft.currentUser?.id };
      const oldChat = draft.byId[chat.id];
      
      // Update the conversation data
      draft.byId[chat.id] = chat;
      
      // Maintain each tab list based on rules
      const maintain = (listName, rule) => {
        const list = draft[listName];
        const inList = list.includes(chat.id);
        const should = rule(chat, context);
        
        // Debug logging for My tab
        if (listName === 'listMy') {
          console.log(`🔍 My tab check for conversation ${chat.id}:`, {
            inList,
            should,
            currentListLength: list.length,
            currentListIds: [...list],
            owner: chat.owner,
            ownerId: typeof chat.owner === 'object' ? chat.owner?.id : chat.owner,
            currentUserId: context.me
          });
        }
        
        if (should && !inList) {
          // Add to list
          list.unshift(chat.id);
          console.log(`➕ Added ${chat.id} to ${listName} (list now has ${list.length} items)`);
        } else if (should && inList) {
          // Already in list, just log for debugging
          if (listName === 'listMy') {
            console.log(`ℹ️ Conversation ${chat.id} already in ${listName}, skipping add`);
          }
        }
        
        if (!should && inList) {
          // Remove from list
          const tabName = listName === 'listAll' ? 'All'
                       : listName === 'listMy' ? 'My'
                       : listName === 'listInbox' ? 'Inbox'
                       : 'Alerts';
          
          // If this conversation was open in the tab it's being removed from, clear it
          if (draft.selectedByTab[tabName] === chat.id) {
            console.log(`🗑️ Clearing conversation ${chat.id} from ${tabName} tab (no longer belongs)`);
            draft.selectedByTab[tabName] = null;
            
            // Call clear function if provided
            if (typeof clearIfOpen === 'function') {
              clearIfOpen(tabName, chat.id, activeTab);
            }
          }
          
          const index = list.indexOf(chat.id);
          if (index >= 0) {
            list.splice(index, 1);
            console.log(`➖ Removed ${chat.id} from ${listName}`);
          }
        }
      };
      
      // Update all tab lists
      maintain('listAll', tabRules.All);
      maintain('listMy', tabRules.My);
      maintain('listInbox', tabRules.Inbox);
      maintain('listAlerts', tabRules.Alerts);
      
      // Deduplicate and re-sort all lists (safety check to prevent duplicates)
      const deduplicate = (list) => {
        const seen = new Set();
        return list.filter(id => {
          if (seen.has(id)) {
            console.warn(`⚠️ Duplicate conversation ID ${id} found in list, removing duplicate`);
            return false;
          }
          seen.add(id);
          return true;
        });
      };
      
      draft.listAll = sortByLastMsg(deduplicate(draft.listAll), draft.byId);
      draft.listMy = sortByLastMsg(deduplicate(draft.listMy), draft.byId);
      draft.listInbox = sortByLastMsg(deduplicate(draft.listInbox), draft.byId);
      draft.listAlerts = sortByLastMsg(deduplicate(draft.listAlerts), draft.byId);
    }),

    // Remove a conversation from store + all tab lists (e.g. conversation.removed)
    removeFromEvent: (conversationId, activeTab, clearIfOpen) => set(draft => {
      const id = Number(conversationId);
      if (!id) return;

      // Clear if open in any tab
      const tabNames = ['All', 'My', 'Inbox', 'Alerts'];
      tabNames.forEach((tabName) => {
        if (draft.selectedByTab?.[tabName] === id) {
          draft.selectedByTab[tabName] = null;
          if (typeof clearIfOpen === 'function') {
            clearIfOpen(tabName, id, activeTab);
          }
        }
      });

      // Remove from lists
      const lists = ['listAll', 'listMy', 'listInbox', 'listAlerts'];
      lists.forEach((listName) => {
        const list = draft[listName];
        const idx = Array.isArray(list) ? list.indexOf(id) : -1;
        if (idx >= 0) list.splice(idx, 1);
      });

      // Remove from byId
      if (draft.byId?.[id]) {
        delete draft.byId[id];
      }
    }),

    // Append a scroll-triggered "next page" of conversations into a specific tab's
    // list (cursor-pagination load-more). Two independent presence checks:
    //  - byId: only set if NOT already cached, so a possibly-stale paginated page
    //    can never clobber fresher real-time/WebSocket data.
    //  - the target list: only push ids not already present in THAT list — a
    //    conversation can already be cached in byId (e.g. via another tab, or
    //    Spam's cacheConversation) while still being legitimately new to this tab.
    // Server-side endpoint filtering is trusted (no tabRules re-check), same trust
    // model the initial snapshot already uses.
    appendTabPage: (tabName, conversations, user) => set(draft => {
      if (!Array.isArray(conversations) || conversations.length === 0) return;

      const listKey = tabName === 'All' ? 'listAll'
        : tabName === 'My' ? 'listMy'
        : tabName === 'Inbox' ? 'listInbox'
        : tabName === 'Alerts' ? 'listAlerts'
        : null;
      if (!listKey) return;

      if (user) {
        draft.currentUser = user;
      }

      const list = draft[listKey];
      conversations.forEach((c) => {
        if (!c || c.id == null) return;
        if (!draft.byId[c.id]) {
          draft.byId[c.id] = c;
        }
        if (!list.includes(c.id)) {
          list.push(c.id);
        }
      });

      // Deduplicate (safety, mirrors upsertFromEvent) then re-sort for consistency
      const seen = new Set();
      const deduped = list.filter((id) => {
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });

      draft[listKey] = sortByLastMsg(deduped, draft.byId);
    }),

    // Cache a conversation's full data into byId WITHOUT adding it to any tab list.
    // Used by the Spam feature so opening a spam conversation can resolve its data
    // (handleChatSelect / selectedChat re-sync) without it leaking into All/My/etc lists.
    cacheConversation: (chat) => set(draft => {
      if (chat && chat.id != null) {
        draft.byId[chat.id] = chat;
      }
    }),

    // Set selected conversation for a tab
    setSelectedForTab: (tabName, idOrNull) => set(draft => {
      console.log(`🔄 Setting selected conversation for ${tabName}:`, idOrNull);
      draft.selectedByTab[tabName] = idOrNull;
    }),
    
    // Get conversation by ID
    getConversation: (id) => {
      const state = get();
      return state.byId[id] || null;
    },
    
    // Get list for a specific tab
    getTabList: (tabName) => {
      const state = get();
      switch (tabName) {
        case 'All': return state.listAll;
        case 'My': return state.listMy;
        case 'Inbox': return state.listInbox;
        case 'Alerts': return state.listAlerts;
        default: return [];
      }
    },
    
    // Get conversations for a tab (with full data)
    getTabConversations: (tabName) => {
      const state = get();
      const ids = state.getTabList(tabName);
      return ids.map(id => state.byId[id]).filter(Boolean);
    },
    
    // Clear all data (for logout)
    clearAll: () => set(draft => {
      draft.byId = {};
      draft.listAll = [];
      draft.listMy = [];
      draft.listInbox = [];
      draft.listAlerts = [];
      draft.selectedByTab = { All: null, My: null, Inbox: null, Alerts: null };
      draft.currentUser = null;
    }),
    
    // Get store statistics
    getStats: () => {
      const state = get();
      return {
        totalConversations: Object.keys(state.byId).length,
        tabCounts: {
          All: state.listAll.length,
          My: state.listMy.length,
          Inbox: state.listInbox.length,
          Alerts: state.listAlerts.length
        },
        selectedByTab: state.selectedByTab
      };
    }
  }))
);

